import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { AvisoTelaComputador } from '@/shared/ui/AvisoTelaComputador/AvisoTelaComputador';
import { ForbiddenPage, NotFoundPage } from '@/app/ErrorPages';
import { ALUNO_ACAO, ALUNO_ACOES_MODULO } from '@/features/alunos/alunoAcoes';
import { ATIVIDADES_ACOES_FEED } from '@/features/atividades/atividadesAcoes';
import { CARDAPIO_ACOES_MODULO } from '@/features/cardapio/cardapioAcoes';
import { CLASSIFICACAO_ACAO, CLASSIFICACAO_ACOES_MODULO } from '@/features/classificacao/classificacaoAcoes';
import { ESCOLA_ACAO, ESCOLA_ACOES_MODULO } from '@/features/escolas/escolaAcoes';
import { COLABORADOR_ACAO, COLABORADOR_ACOES_MODULO } from '@/features/colaboradores/colaboradorAcoes';
import { CARGO_ACAO, CARGO_ACOES_MODULO } from '@/features/cargos/cargoAcoes';
import {
  LAYOUT_EMAIL_ACAO,
  LAYOUT_EMAIL_ACOES_MODULO,
} from '@/features/layouts-email/layoutEmailAcoes';
import { AUDITORIA_ACOES_MODULO } from '@/features/auditoria/auditoriaAcoes';
import { ATA_ACAO, ATA_ACOES_MODULO } from '@/features/atas/ataAcoes';
import { CHAT_ACAO } from '@/features/chat/chatAcoes';
import { PERFIL_ACAO, PERFIL_ACOES_MODULO } from '@/features/perfis/perfilAcoes';
import { PRATO_ACAO, PRATO_ACOES_MODULO } from '@/features/pratos/pratoAcoes';
import { RESPONSAVEL_ACAO, RESPONSAVEL_ACOES_MODULO } from '@/features/responsaveis/responsavelAcoes';
import { TURMA_ACAO, TURMA_ACOES_MODULO } from '@/features/turmas/turmaAcoes';
import { RELATORIO_ACAO, RELATORIO_ACOES_MENU } from '@/features/relatorios/relatoriosCatalogo';
import { ProtectedRoute } from '@/shared/layout/ProtectedRoute';
import { RequireAcoes } from '@/shared/layout/RequireAcoes';
import { Spinner } from '@/shared/ui';

const LoginPage = lazy(() =>
  import('@/features/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const EsqueciSenhaPage = lazy(() =>
  import('@/features/auth/EsqueciSenhaPage').then((m) => ({ default: m.EsqueciSenhaPage })),
);
const DefinirSenhaPage = lazy(() =>
  import('@/features/auth/DefinirSenhaPage').then((m) => ({ default: m.DefinirSenhaPage })),
);
const EscolherEscolaPage = lazy(() =>
  import('@/features/auth/EscolherEscolaPage').then((m) => ({ default: m.EscolherEscolaPage })),
);
const PrimeiroAcessoPage = lazy(() =>
  import('@/features/auth/PrimeiroAcessoPage').then((m) => ({ default: m.PrimeiroAcessoPage })),
);
const DashboardPage = lazy(() =>
  import('@/features/dashboard/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const RelatoriosPage = lazy(() =>
  import('@/features/relatorios/RelatoriosPage').then((m) => ({ default: m.RelatoriosPage })),
);
const RelatorioAtividadesPage = lazy(() =>
  import('@/features/relatorios/RelatorioAtividadesPage').then((m) => ({
    default: m.RelatorioAtividadesPage,
  })),
);
const RelatorioAtividadesVisualizacaoPage = lazy(() =>
  import('@/features/relatorios/RelatorioAtividadesVisualizacaoPage').then((m) => ({
    default: m.RelatorioAtividadesVisualizacaoPage,
  })),
);
const RelatorioAcessosPage = lazy(() =>
  import('@/features/relatorios/RelatorioAcessosPage').then((m) => ({
    default: m.RelatorioAcessosPage,
  })),
);
const RelatorioAcessosVisualizacaoPage = lazy(() =>
  import('@/features/relatorios/RelatorioAcessosVisualizacaoPage').then((m) => ({
    default: m.RelatorioAcessosVisualizacaoPage,
  })),
);
const EscolasListPage = lazy(() =>
  import('@/features/escolas/EscolasListPage').then((m) => ({ default: m.EscolasListPage })),
);
const EscolaNovaPage = lazy(() =>
  import('@/features/escolas/EscolaFormPage').then((m) => ({ default: m.EscolaNovaPage })),
);
const EscolaEditarPage = lazy(() =>
  import('@/features/escolas/EscolaFormPage').then((m) => ({ default: m.EscolaEditarPage })),
);
const TurmasListPage = lazy(() =>
  import('@/features/turmas/TurmasListPage').then((m) => ({ default: m.TurmasListPage })),
);
const TurmaNovaPage = lazy(() =>
  import('@/features/turmas/TurmaFormPage').then((m) => ({ default: m.TurmaNovaPage })),
);
const TurmaEditarPage = lazy(() =>
  import('@/features/turmas/TurmaFormPage').then((m) => ({ default: m.TurmaEditarPage })),
);
const AlunosListPage = lazy(() =>
  import('@/features/alunos/AlunosListPage').then((m) => ({ default: m.AlunosListPage })),
);
const AlunoNovaPage = lazy(() =>
  import('@/features/alunos/AlunoFormPage').then((m) => ({ default: m.AlunoNovaPage })),
);
const AlunoEditarPage = lazy(() =>
  import('@/features/alunos/AlunoFormPage').then((m) => ({ default: m.AlunoEditarPage })),
);
const ResponsaveisListPage = lazy(() =>
  import('@/features/responsaveis/ResponsaveisListPage').then((m) => ({
    default: m.ResponsaveisListPage,
  })),
);
const ResponsavelNovaPage = lazy(() =>
  import('@/features/responsaveis/ResponsavelFormPage').then((m) => ({
    default: m.ResponsavelNovaPage,
  })),
);
const ResponsavelEditarPage = lazy(() =>
  import('@/features/responsaveis/ResponsavelFormPage').then((m) => ({
    default: m.ResponsavelEditarPage,
  })),
);
const ColaboradoresListPage = lazy(() =>
  import('@/features/colaboradores/ColaboradoresListPage').then((m) => ({
    default: m.ColaboradoresListPage,
  })),
);
const ColaboradorNovaPage = lazy(() =>
  import('@/features/colaboradores/ColaboradorFormPage').then((m) => ({
    default: m.ColaboradorNovaPage,
  })),
);
const ColaboradorEditarPage = lazy(() =>
  import('@/features/colaboradores/ColaboradorFormPage').then((m) => ({
    default: m.ColaboradorEditarPage,
  })),
);
const CargosListPage = lazy(() =>
  import('@/features/cargos/CargosListPage').then((m) => ({ default: m.CargosListPage })),
);
const CargoNovaPage = lazy(() =>
  import('@/features/cargos/CargoFormPage').then((m) => ({ default: m.CargoNovaPage })),
);
const CargoEditarPage = lazy(() =>
  import('@/features/cargos/CargoFormPage').then((m) => ({ default: m.CargoEditarPage })),
);
const PerfisListPage = lazy(() =>
  import('@/features/perfis/PerfisListPage').then((m) => ({ default: m.PerfisListPage })),
);
const PerfilNovaPage = lazy(() =>
  import('@/features/perfis/PerfilFormPage').then((m) => ({ default: m.PerfilNovaPage })),
);
const PerfilEditarPage = lazy(() =>
  import('@/features/perfis/PerfilFormPage').then((m) => ({ default: m.PerfilEditarPage })),
);
const ClassificacoesListPage = lazy(() =>
  import('@/features/classificacao/ClassificacoesListPage').then((m) => ({
    default: m.ClassificacoesListPage,
  })),
);
const ClassificacaoNovaPage = lazy(() =>
  import('@/features/classificacao/ClassificacaoFormPage').then((m) => ({
    default: m.ClassificacaoNovaPage,
  })),
);
const ClassificacaoEditarPage = lazy(() =>
  import('@/features/classificacao/ClassificacaoFormPage').then((m) => ({
    default: m.ClassificacaoEditarPage,
  })),
);
const PratosListPage = lazy(() =>
  import('@/features/pratos/PratosListPage').then((m) => ({ default: m.PratosListPage })),
);
const PratoNovaPage = lazy(() =>
  import('@/features/pratos/PratoFormPage').then((m) => ({ default: m.PratoNovaPage })),
);
const PratoEditarPage = lazy(() =>
  import('@/features/pratos/PratoFormPage').then((m) => ({ default: m.PratoEditarPage })),
);
const CardapioPage = lazy(() =>
  import('@/features/cardapio/CardapioPage').then((m) => ({ default: m.CardapioPage })),
);
const AtividadesPage = lazy(() =>
  import('@/features/atividades/AtividadesPage').then((m) => ({ default: m.AtividadesPage })),
);
const LayoutsEmailListPage = lazy(() =>
  import('@/features/layouts-email/LayoutsEmailListPage').then((m) => ({
    default: m.LayoutsEmailListPage,
  })),
);
const LayoutEmailNovaPage = lazy(() =>
  import('@/features/layouts-email/LayoutEmailFormPage').then((m) => ({
    default: m.LayoutEmailNovaPage,
  })),
);
const LayoutEmailEditarPage = lazy(() =>
  import('@/features/layouts-email/LayoutEmailFormPage').then((m) => ({
    default: m.LayoutEmailEditarPage,
  })),
);
const ConfiguracoesPage = lazy(() =>
  import('@/features/configuracoes/ConfiguracoesPage').then((m) => ({
    default: m.ConfiguracoesPage,
  })),
);
const AuditoriaListPage = lazy(() =>
  import('@/features/auditoria/AuditoriaListPage').then((m) => ({
    default: m.AuditoriaListPage,
  })),
);
const ChatPage = lazy(() =>
  import('@/features/chat/ChatPage').then((m) => ({ default: m.ChatPage })),
);
const AgendaPage = lazy(() =>
  import('@/features/agenda/AgendaPage').then((m) => ({ default: m.AgendaPage })),
);
const AtasListPage = lazy(() =>
  import('@/features/atas/AtasListPage').then((m) => ({ default: m.AtasListPage })),
);
const AtaNovaPage = lazy(() =>
  import('@/features/atas/AtaFormPage').then((m) => ({ default: m.AtaNovaPage })),
);
const AtaEditarPage = lazy(() =>
  import('@/features/atas/AtaFormPage').then((m) => ({ default: m.AtaEditarPage })),
);
const AtaImprimirPage = lazy(() =>
  import('@/features/atas/AtaImprimirPage').then((m) => ({ default: m.AtaImprimirPage })),
);

/** Link no formato da familia (`/publicacao/:id`) abre as atividades da escola. */
function RedirectPublicacaoSingular() {
  const { id } = useParams();
  if (!id || !/^\d+$/.test(id)) return <NotFoundPage />;
  return <Navigate to={`/publicacoes?idPublicacao=${id}`} replace />;
}

function Lazy({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<Spinner fullViewport message="Carregando…" />}>{children}</Suspense>;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <Lazy>
            <LoginPage />
          </Lazy>
        }
      />
      <Route
        path="/esqueci-senha"
        element={
          <Lazy>
            <EsqueciSenhaPage />
          </Lazy>
        }
      />
      <Route
        path="/ativar"
        element={
          <Lazy>
            <DefinirSenhaPage />
          </Lazy>
        }
      />
      <Route
        path="/redefinir-senha"
        element={
          <Lazy>
            <DefinirSenhaPage />
          </Lazy>
        }
      />
      <Route
        path="/escolher-escola"
        element={
          <Lazy>
            <EscolherEscolaPage />
          </Lazy>
        }
      />
      <Route
        path="/primeiro-acesso"
        element={
          <Lazy>
            <PrimeiroAcessoPage />
          </Lazy>
        }
      />

      <Route element={<ProtectedRoute />}>
        <Route
          index
          element={
            <Lazy>
              <DashboardPage />
            </Lazy>
          }
        />
        <Route path="dashboard" element={<Navigate to="/" replace />} />

        <Route element={<RequireAcoes acoes={[...ESCOLA_ACOES_MODULO]} />}>
          <Route
            path="escolas"
            element={
              <Lazy>
                <EscolasListPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[ESCOLA_ACAO.CRIAR]} />}>
          <Route
            path="escolas/nova"
            element={
              <Lazy>
                <EscolaNovaPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...ESCOLA_ACOES_MODULO]} />}>
          <Route
            path="escolas/:id"
            element={
              <Lazy>
                <EscolaEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...TURMA_ACOES_MODULO]} />}>
          <Route
            path="turmas"
            element={
              <Lazy>
                <TurmasListPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[TURMA_ACAO.CRIAR]} />}>
          <Route
            path="turmas/nova"
            element={
              <Lazy>
                <TurmaNovaPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...TURMA_ACOES_MODULO]} />}>
          <Route
            path="turmas/:id"
            element={
              <Lazy>
                <TurmaEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...ALUNO_ACOES_MODULO]} />}>
          <Route
            path="alunos"
            element={
              <Lazy>
                <AlunosListPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[ALUNO_ACAO.CRIAR]} />}>
          <Route
            path="alunos/nova"
            element={
              <Lazy>
                <AlunoNovaPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...ALUNO_ACOES_MODULO]} />}>
          <Route
            path="alunos/:id"
            element={
              <Lazy>
                <AlunoEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...RESPONSAVEL_ACOES_MODULO]} />}>
          <Route
            path="responsaveis"
            element={
              <Lazy>
                <ResponsaveisListPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[RESPONSAVEL_ACAO.CRIAR]} />}>
          <Route
            path="responsaveis/nova"
            element={
              <Lazy>
                <ResponsavelNovaPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...RESPONSAVEL_ACOES_MODULO]} />}>
          <Route
            path="responsaveis/:id"
            element={
              <Lazy>
                <ResponsavelEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...COLABORADOR_ACOES_MODULO]} />}>
          <Route
            path="colaboradores"
            element={
              <Lazy>
                <ColaboradoresListPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[COLABORADOR_ACAO.CRIAR]} />}>
          <Route
            path="colaboradores/nova"
            element={
              <Lazy>
                <ColaboradorNovaPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...COLABORADOR_ACOES_MODULO]} />}>
          <Route
            path="colaboradores/:id"
            element={
              <Lazy>
                <ColaboradorEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...CARGO_ACOES_MODULO]} />}>
          <Route
            path="cargos"
            element={
              <Lazy>
                <CargosListPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[CARGO_ACAO.CRIAR]} />}>
          <Route
            path="cargos/nova"
            element={
              <Lazy>
                <CargoNovaPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...CARGO_ACOES_MODULO]} />}>
          <Route
            path="cargos/:id"
            element={
              <Lazy>
                <CargoEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...PERFIL_ACOES_MODULO]} />}>
          <Route
            path="perfis"
            element={
              <Lazy>
                <PerfisListPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[PERFIL_ACAO.CRIAR]} />}>
          <Route
            path="perfis/nova"
            element={
              <Lazy>
                <PerfilNovaPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...PERFIL_ACOES_MODULO]} />}>
          <Route
            path="perfis/:id"
            element={
              <Lazy>
                <PerfilEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...ATIVIDADES_ACOES_FEED]} />}>
          <Route
            path="publicacoes"
            element={
              <Lazy>
                <AtividadesPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...PRATO_ACOES_MODULO]} />}>
          <Route
            path="pratos"
            element={
              <Lazy>
                <PratosListPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[PRATO_ACAO.CRIAR]} />}>
          <Route
            path="pratos/nova"
            element={
              <Lazy>
                <PratoNovaPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[...PRATO_ACOES_MODULO]} />}>
          <Route
            path="pratos/:id"
            element={
              <Lazy>
                <PratoEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...CLASSIFICACAO_ACOES_MODULO]} />}>
          <Route
            path="classificacao"
            element={
              <Lazy>
                <ClassificacoesListPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[CLASSIFICACAO_ACAO.CRIAR]} />}>
          <Route
            path="classificacao/nova"
            element={
              <Lazy>
                <ClassificacaoNovaPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[...CLASSIFICACAO_ACOES_MODULO]} />}>
          <Route
            path="classificacao/:id"
            element={
              <Lazy>
                <ClassificacaoEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...CARDAPIO_ACOES_MODULO]} />}>
          <Route
            path="cardapio"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <CardapioPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...LAYOUT_EMAIL_ACOES_MODULO]} />}>
          <Route
            path="layouts-email"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <LayoutsEmailListPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[LAYOUT_EMAIL_ACAO.CRIAR]} />}>
          <Route
            path="layouts-email/nova"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <LayoutEmailNovaPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[...LAYOUT_EMAIL_ACOES_MODULO]} />}>
          <Route
            path="layouts-email/:id"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <LayoutEmailEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...AUDITORIA_ACOES_MODULO]} />}>
          <Route
            path="auditoria"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <AuditoriaListPage />
              </Lazy>
            }
          />
        </Route>

        <Route
          path="agenda"
          element={
            <Lazy>
              <AgendaPage />
            </Lazy>
          }
        />

        <Route element={<RequireAcoes acoes={[CHAT_ACAO.VER]} />}>
          <Route
            path="chat"
            element={
              <Lazy>
                <ChatPage />
              </Lazy>
            }
          />
          <Route
            path="chat/:idConversa"
            element={
              <Lazy>
                <ChatPage />
              </Lazy>
            }
          />
        </Route>

        <Route element={<RequireAcoes acoes={[...ATA_ACOES_MODULO]} />}>
          <Route
            path="atas"
            element={
              <Lazy>
                <AtasListPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[ATA_ACAO.CRIAR_EDITAR]} />}>
          <Route
            path="atas/nova"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <AtaNovaPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[...ATA_ACOES_MODULO]} />}>
          <Route
            path="atas/:id"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <AtaEditarPage />
              </Lazy>
            }
          />
        </Route>

        <Route
          path="configuracoes"
          element={
            <Lazy>
              <AvisoTelaComputador />
                <ConfiguracoesPage />
            </Lazy>
          }
        />

        <Route element={<RequireAcoes acoes={[...RELATORIO_ACOES_MENU]} />}>
          <Route
            path="relatorios"
            element={
              <Lazy>
                <RelatoriosPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[RELATORIO_ACAO.ATIVIDADES]} />}>
          <Route
            path="relatorios/atividades"
            element={
              <Lazy>
                <RelatorioAtividadesPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[RELATORIO_ACAO.ACESSOS]} />}>
          <Route
            path="relatorios/acessos"
            element={
              <Lazy>
                <RelatorioAcessosPage />
              </Lazy>
            }
          />
        </Route>
      </Route>

      <Route element={<ProtectedRoute semShell />}>
        <Route element={<RequireAcoes acoes={[RELATORIO_ACAO.ATIVIDADES]} />}>
          <Route
            path="relatorios/atividades/visualizar"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <RelatorioAtividadesVisualizacaoPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[RELATORIO_ACAO.ACESSOS]} />}>
          <Route
            path="relatorios/acessos/visualizar"
            element={
              <Lazy>
                <AvisoTelaComputador />
                <RelatorioAcessosVisualizacaoPage />
              </Lazy>
            }
          />
        </Route>
        <Route element={<RequireAcoes acoes={[...ATA_ACOES_MODULO]} />}>
          <Route
            path="atas/:id/imprimir"
            element={
              <Lazy>
                <AtaImprimirPage />
              </Lazy>
            }
          />
        </Route>
      </Route>

      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/publicacao/:id" element={<RedirectPublicacaoSingular />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
